import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { GraduationCap, Save, Plus, Trash2, Globe, MapPin } from "lucide-react";
import CoachEarningsCard from "@/components/CoachEarningsCard";

const DAYS = [
  { id: "mon", label: "Mon" }, { id: "tue", label: "Tue" }, { id: "wed", label: "Wed" },
  { id: "thu", label: "Thu" }, { id: "fri", label: "Fri" }, { id: "sat", label: "Sat" },
  { id: "sun", label: "Sun" },
];

const SPECIALTY_OPTIONS = [
  "Pitch Design", "Mechanics", "Velocity Development",
  "Arm Care", "Pitch Mix Strategy", "Recruiting / Showcase",
];

const fmtUsd = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

export default function CoachLessonOfferings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState({});
  const [feePct, setFeePct] = useState(35);
  const [offering, setOffering] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coaches/me/lesson-offerings");
      setOffering(data.offering);
      setCatalog(data.catalog);
      setFeePct(data.platform_fee_pct);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (patch) => setOffering((prev) => ({ ...prev, ...patch }));

  const toggleType = (id) => {
    const cur = offering.enabled_lesson_types || [];
    set({ enabled_lesson_types: cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id] });
  };

  const toggleSpecialty = (spec) => {
    const cur = offering.specialties || [];
    set({ specialties: cur.includes(spec) ? cur.filter((t) => t !== spec) : [...cur, spec] });
  };

  const addWindow = () => set({ availability: [...(offering.availability || []), { day: "mon", start_hhmm: "16:00", end_hhmm: "19:00" }] });
  const updateWindow = (i, patch) => set({ availability: offering.availability.map((w, j) => (j === i ? { ...w, ...patch } : w)) });
  const removeWindow = (i) => set({ availability: offering.availability.filter((_, j) => j !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        is_active: !!offering.is_active,
        enabled_lesson_types: offering.enabled_lesson_types || [],
        bio: offering.bio || null,
        headshot_url: offering.headshot_url || null,
        timezone: offering.timezone || "America/New_York",
        location_city: offering.location_city || null,
        contact_email: offering.contact_email || null,
        availability: offering.availability || [],
        specialties: offering.specialties || [],
      };
      const { data } = await api.put("/coaches/me/lesson-offerings", payload);
      setOffering(data.offering);
      toast.success("Lesson offerings saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading || !offering) {
    return <div className="text-white/40 text-sm p-6">Loading lesson offerings…</div>;
  }

  const enabled = offering.enabled_lesson_types || [];
  const inputCls = "bg-[var(--ink-1)] border-[var(--ink-border)] text-white";

  return (
    <div className="space-y-6" data-testid="coach-lesson-offerings-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>Workstream 2 · Marketplace</div>
          <h1 className="font-display font-black text-3xl tracking-tight text-white mt-1 flex items-center gap-2">
            <GraduationCap className="h-7 w-7" /> Lesson Offerings
          </h1>
          <p className="text-sm text-white/60 mt-1 max-w-2xl">
            Opt into the ArmIQ Pitching Lessons marketplace. Set the lesson types you teach, your availability,
            and your bio. Students can book + pay directly through ArmIQ. Platform fee: <b className="text-white">{feePct}%</b> — coach payout: <b className="text-white">{100 - feePct}%</b>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="offering-active-toggle">
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-white/60">Listing active</span>
            <Switch checked={!!offering.is_active} onCheckedChange={(v) => set({ is_active: v })} />
          </label>
          <Button onClick={save} disabled={saving} className="text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="save-offerings-button">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {/* Lesson type catalog */}
      <section className="rounded-xl ink-card p-5">
        <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Lesson types you offer</div>
        <p className="text-xs text-white/50 mt-1">Pricing is server-fixed across the platform. Check the boxes for the formats you want to teach.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          {Object.entries(catalog).map(([id, c]) => {
            const isOn = enabled.includes(id);
            const payout = Math.round(c.price_cents * (100 - feePct) / 100);
            return (
              <button
                key={id}
                type="button"
                onClick={() => toggleType(id)}
                className={`text-left rounded-xl p-4 border transition ${isOn ? "border-cyan-400/60 bg-cyan-400/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}
                data-testid={`offering-type-${id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-white">{c.label}</div>
                    <div className="text-xs text-white/55 mt-0.5">
                      {c.duration_min} min · {c.max_seats === 1 ? "1:1" : `up to ${c.max_seats} seats`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-black text-xl text-white">{fmtUsd(c.price_cents)}</div>
                    <div className="text-[10px] text-white/40 uppercase tracking-wide">student price</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-[11px]">
                  <span className="text-white/45">You earn: <b className="text-white">{fmtUsd(payout)}</b></span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold uppercase tracking-wide text-[10px]
                    ${isOn ? "bg-cyan-400/15 text-cyan-300" : "bg-white/5 text-white/40"}`}>
                    {isOn ? "Enabled" : "Off"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Profile card */}
        <section className="rounded-xl ink-card p-5">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Public profile</div>
          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-white/70">Bio</Label>
              <Textarea
                value={offering.bio || ""}
                onChange={(e) => set({ bio: e.target.value })}
                maxLength={1500}
                placeholder="Tell students about your coaching philosophy, credentials, and what they should expect…"
                className={`mt-1.5 min-h-[120px] ${inputCls}`}
                data-testid="offering-bio"
              />
              <div className="text-[10px] text-white/35 text-right mt-1">{(offering.bio || "").length}/1500</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70 flex items-center gap-1"><MapPin className="h-3 w-3" /> Location (city)</Label>
                <Input value={offering.location_city || ""} onChange={(e) => set({ location_city: e.target.value })}
                  placeholder="Williamsport, PA" className={`mt-1.5 ${inputCls}`} data-testid="offering-city" />
              </div>
              <div>
                <Label className="text-white/70 flex items-center gap-1"><Globe className="h-3 w-3" /> Timezone</Label>
                <Input value={offering.timezone || ""} onChange={(e) => set({ timezone: e.target.value })}
                  placeholder="America/New_York" className={`mt-1.5 ${inputCls}`} data-testid="offering-tz" />
              </div>
            </div>
            <div>
              <Label className="text-white/70">Contact email (shown to booked students)</Label>
              <Input type="email" value={offering.contact_email || ""} onChange={(e) => set({ contact_email: e.target.value })}
                className={`mt-1.5 ${inputCls}`} data-testid="offering-email" />
            </div>
          </div>
        </section>

        {/* Specialties */}
        <section className="rounded-xl ink-card p-5">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Specialties</div>
          <p className="text-xs text-white/50 mt-1">Pick up to 6 — students filter by these on the marketplace.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {SPECIALTY_OPTIONS.map((s) => {
              const on = (offering.specialties || []).includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSpecialty(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition border ${on ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-200" : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"}`}
                  data-testid={`offering-spec-${s.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </section>
      </div>

      {/* Availability */}
      <section className="rounded-xl ink-card p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Weekly availability</div>
            <p className="text-xs text-white/50 mt-1">Add the recurring windows when you can teach. Students see open slots inside these.</p>
          </div>
          <Button onClick={addWindow} size="sm" variant="outline" className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="offering-add-window">
            <Plus className="h-4 w-4 mr-1" /> Add window
          </Button>
        </div>
        <div className="mt-4 space-y-2">
          {(offering.availability || []).length === 0 && (
            <div className="text-xs text-white/40 italic py-3">No availability windows yet — add at least one before going live.</div>
          )}
          {(offering.availability || []).map((w, i) => (
            <div key={i} className="grid grid-cols-[110px_1fr_1fr_auto] gap-2 items-center" data-testid={`offering-window-${i}`}>
              <select
                value={w.day}
                onChange={(e) => updateWindow(i, { day: e.target.value })}
                className={`h-10 rounded-md px-2 ${inputCls}`}
              >
                {DAYS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
              <Input type="time" value={w.start_hhmm} onChange={(e) => updateWindow(i, { start_hhmm: e.target.value })} className={inputCls} />
              <Input type="time" value={w.end_hhmm} onChange={(e) => updateWindow(i, { end_hhmm: e.target.value })} className={inputCls} />
              <Button onClick={() => removeWindow(i)} variant="ghost" size="sm" className="text-white/50 hover:text-red-400 hover:bg-red-500/10"
                data-testid={`offering-window-remove-${i}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </section>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Coming next in Workstream 2</div>
        <ul className="mt-2 space-y-1 text-sm text-white/70 list-disc pl-5">
          <li><b>Phase 2</b>: Public marketplace page (`/lessons`) — students browse coaches, pick a time slot, pay via Stripe</li>
          <li><b>Phase 3</b>: Stripe Connect Express split-payment + automated coach payouts</li>
        </ul>
      </div>

      {/* Earnings + incoming bookings (Phase 2 + 3) */}
      <div>
        <div className="text-[10px] tracking-[0.3em] font-bold uppercase mt-4 mb-3" style={{ color: "var(--brand-secondary)" }}>Earnings & Bookings</div>
        <CoachEarningsCard />
      </div>
    </div>
  );
}
